import { Button, Stack, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useLocation, useNavigate } from "react-router-dom";
import userService from "../../../api/UserService";
import Image from "../../../common/components/fundamentals/Image";
import {
  borderStyle,
  boxHeight,
  defaultGap,
  perelynPrimary,
  sidebarWidth,
  smallGap,
} from "../../../styles/consts";
import { getFirstPathPart } from "../../../utils";
import { useLayoutConfig } from "../../context/LayoutConfigProvider";
import { useSnackbar } from "../../context/SnackbarProvider";
import { OrangeOnHoverStack } from "../../styles/Box.styles";
import { HeadingText, TitleText } from "../../styles/Text.styles";
import Icon from "../fundamentals/Icon";

export default function Navbar({
  loginPage = false,
  offerTitle,
  offerCompanyName,
  offerUrl,
  isArchived = false,
}: {
  loginPage?: boolean;
  offerTitle?: string;
  offerCompanyName?: string;
  offerUrl?: string;
  isArchived?: boolean;
}) {
  const { layoutConfig } = useLayoutConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleBackToToolsOverviewClick = () => {
    navigate(getFirstPathPart(location.pathname));
  };

  const handleBackToWebsiteClick = () => {
    window.location.href = "https://www.perelyn.com/";
  };

  const handleUseTool = async () => {
    const u = await userService.getUser();
    if (u.active === true) {
      navigate(location.pathname + "/dashboard");
    } else {
      showSnackbar(
        "Your account needs to be activated by administrator in order to access this tool.",
        true,
        false
      );
    }
  };

  const handleSignUpForNews = () => {
    showSnackbar("Signed up for tool news! We will keep you updated.");
  };

  const isValidWebUrl =
    offerUrl &&
    (offerUrl.startsWith("http://") || offerUrl.startsWith("https://"));

  return (
    <Stack
      sx={{
        borderBottom: borderStyle,
        height: boxHeight,
        minHeight: boxHeight,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Stack
        sx={{
          flexDirection: "row",
          alignItems: "center",
          height: "100%",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Button
          variant="transparentBig"
          sx={{
            paddingLeft: defaultGap,
            width: sidebarWidth,
            flexShrink: 0,
            "&&": {
              justifyContent: "start",
            },
          }}
          onClick={handleLogoClick}
        >
          <Image src={loginPage ? "PerelynLogo" : "FyndrLogo"} />
        </Button>
        <Stack
          sx={{
            borderLeft: borderStyle,
            height: "100%",
            flexDirection: "row",
            alignItems: "center",
            paddingLeft: defaultGap,
            paddingRight: defaultGap,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {offerTitle && offerCompanyName ? (
            <Stack sx={{ gap: "0.25rem", minWidth: 0, overflow: "hidden" }}>
              <Typography sx={{ lineHeight: 1 }}>{offerCompanyName}</Typography>
              <Stack sx={{ flexDirection: "row", overflow: "hidden" }}>
                {isArchived && (
                  <HeadingText
                    sx={{
                      color: perelynPrimary,
                      lineHeight: 1.2,
                      cursor: "default",
                      flexShrink: 0,
                      pointerEvents: "none",
                      marginRight: "0.25rem",
                    }}
                  >
                    ARCHIVED -
                  </HeadingText>
                )}
                {isValidWebUrl ? (
                  <OrangeOnHoverStack
                    onClick={() => window.open(offerUrl)}
                    sx={{ overflow: "hidden", minWidth: 0, flexDirection: "row", alignItems: "center", gap: "0.25rem" }}
                  >
                    <HeadingText
                      sx={{
                        cursor: "pointer",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {offerTitle}
                    </HeadingText>
                    <OpenInNewIcon sx={{ fontSize: "1rem", flexShrink: 0 }} />
                  </OrangeOnHoverStack>
                ) : (
                  <Stack sx={{ overflow: "hidden", minWidth: 0 }}>
                    <HeadingText
                      sx={{
                        cursor: "default",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {offerTitle}
                    </HeadingText>
                  </Stack>
                )}
              </Stack>
            </Stack>
          ) : (
            <TitleText
              sx={{
                whiteSpace: "nowrap",
              }}
            >
              {!loginPage && layoutConfig.heading}
            </TitleText>
          )}
        </Stack>
      </Stack>

      <Stack
        sx={{
          height: "100%",
          flexDirection: "row",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {layoutConfig.useToolVisible && (
          <Button
            variant="transparentBig"
            id="use-tool"
            sx={{
              padding: `0 ${smallGap}`,
              borderLeft: borderStyle,
            }}
            onClick={handleUseTool}
          >
            Use Tool
          </Button>
        )}
        {layoutConfig.signUpForNewsVisible && (
          <Button
            variant="transparentBig"
            id="sign-up-for-news"
            sx={{
              padding: `0 ${smallGap}`,
              borderLeft: borderStyle,
            }}
            onClick={handleSignUpForNews}
          >
            Sign up for tool news
          </Button>
        )}

        {layoutConfig.addToFavoriteVisible && (
          <Stack
            sx={{
              height: "100%",
              width: boxHeight,
              justifyContent: "center",
              alignItems: "center",
              borderLeft: borderStyle,
            }}
          >
            <Icon name="Heart" sx={{ height: "1.5rem", width: "1.5rem" }} />
          </Stack>
        )}
        {layoutConfig.backToVisible && (
          <Button
            variant="transparentBig"
            sx={{
              padding: `0 ${smallGap}`,
              borderLeft: borderStyle,
            }}
            onClick={
              loginPage
                ? handleBackToWebsiteClick
                : handleBackToToolsOverviewClick
            }
          >
            Back to {loginPage ? "website" : "tools overview"}
          </Button>
        )}

        <Stack
          sx={{ height: "100%", width: "2.5rem", borderLeft: borderStyle }}
        >
          <Button
            variant="transparentSmall"
            onClick={handleBackToWebsiteClick}
            sx={{
              "&&": {
                transition: "all 0s",
              },
            }}
          >
            <Icon name="Globe" sx={{ height: "1.5rem", width: "1.5rem" }} />
          </Button>
          <Button
            variant="transparentSmall"
            sx={{
              "&&": {
                transition: "all 0s",
              },
              borderTop: borderStyle,
            }}
          >
            <Icon name="Magnifier" sx={{ height: "1.5rem", width: "1.5rem" }} />
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
